import { useEffect, useState } from "react";
import dayjs from "dayjs";
import sessionHandling from "services/utils/notificationUtils";
import { setCachedPayInputConfig } from "pages/PayRoll/Configuration/PayInputConfiguration/payInputConfigApi";

// Cache key for the reimbursement settings in the shared pay-input config
// cache (read by the sidebar to hide Pay Inputs > Reimbursements).
export const REIMBURSEMENT_PAY_INPUT_TYPE = "REIMBURSEMENT";

// API calls for the three Reimbursement screens:
//   Payroll > Configuration > Pay Input > Reimbursements
//     /api/payroll/configuration/pay-input/reimbursement/ (settings, types, pay-components)
//   My Workspace > My Requests > Reimbursement
//     /api/expense/ (own claims, submit, cancel) + /api/expense-attachment/{id}/
//   Team Workspace > Approvals > Reimbursement
//     /api/team_zone/approve/expense/teamworkspace-expense-approve/ (list, review)
// Claims come from the old expense tables. Fields the backend does not send
// yet (bill_number, pay_month, added_to_payroll_at, paid_at, amount_approved)
// are read when present, so the screens fill in once the backend adds them.

const CONFIG_BASE = "payroll/configuration/pay-input/reimbursement/";
const TEAM_CLAIMS = "team_zone/approve/expense/teamworkspace-expense-approve/";

export class ReimbursementApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors || {};
  }
}

const textOf = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(textOf).filter(Boolean).join(" ");
  if (typeof value === "object") return Object.values(value).map(textOf).filter(Boolean).join(" ");
  return String(value);
};

// Reads both error formats: the new config API ({code, message, errors:[...],
// meta.detail}) and the old claim APIs ({error}, {detail} or DRF field errors).
const parseError = (body, status) => {
  const fieldErrors = {};
  let message = "";
  let code = body?.code;
  if (Array.isArray(body?.errors)) {
    const messages = [];
    body.errors.forEach((item) => {
      code = code || item?.code;
      const detail = item?.meta?.detail;
      if (detail && typeof detail === "object") {
        Object.entries(detail).forEach(([field, value]) => { fieldErrors[field] = textOf(value); });
      }
      if (item?.field) fieldErrors[item.field] = item.message;
      if (item?.message) messages.push(item.message);
    });
    const detailText = Object.values(fieldErrors).join(" ");
    message = messages.filter((text) => text !== "Check the highlighted fields.").join(" ") || detailText || messages.join(" ");
  } else if (body?.error || body?.message || body?.detail) {
    message = textOf(body.error || body.message || body.detail);
  } else if (body && typeof body === "object") {
    Object.entries(body).forEach(([field, value]) => { fieldErrors[field] = textOf(value); });
    message = Object.values(fieldErrors).join(" ");
  }
  return new ReimbursementApiError(message || `Request failed (${status}). Please try again.`, { status, code, fieldErrors });
};

const buildUrl = (domain, path, query) => {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.append(key, value);
  });
  const search = params.toString();
  return `${String(domain || "").replace(/\/$/, "")}/${path}${search ? `?${search}` : ""}`;
};

const request = async (domain, path, { method = "GET", query, body, form, signal } = {}) => {
  const headers = { Authorization: `Token ${localStorage.getItem("token")}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const response = await fetch(buildUrl(domain, path, query), {
    method,
    signal,
    headers,
    ...(form ? { body: form } : body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new ReimbursementApiError("Your session has expired. Please sign in again.", { status: 401 });
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = parseError(data, response.status);
    // 409 is "logged in elsewhere" on this server, except TYPE_IN_USE on delete.
    if (response.status === 409 && error.code !== "TYPE_IN_USE") sessionHandling();
    throw error;
  }
  return data;
};

const toNumber = (value) => (value === null || value === undefined || value === "" ? null : Number(value));
const toAmountString = (value) => (value === null || value === undefined || value === "" ? null : Number(value).toFixed(2));

// ---------- Configuration: settings ----------

export const fromApiSettings = (data) => ({
  is_configured: Boolean(data.is_configured),
  enabled: data.is_enabled !== false,
  treatment: data.default_treatment,
  treatmentOptions: data.treatment_options || [],
  approvalRequired: data.approval_required ? "yes" : "no",
  approvalConfigurable: true,
  billUploadEnabled: data.bill_upload_enabled !== false,
  sourceOptions: (data.source_details || []).map((source) => ({
    value: source.source_type,
    label: source.label,
    comingSoon: !source.is_available || source.source_type === "ERP",
  })),
  sources: (data.source_details || [])
    .filter((source) => source.is_available && source.source_type !== "ERP" && data.allowed_sources?.[source.source_type])
    .map((source) => source.source_type),
});

// Same call shape as requestPayInputConfig, so the Pay Input screen can use
// either: GET without `config`, PUT with it. Bill upload and the HR role are
// not on the screen, so they are left out of the PUT and keep their values.
export const requestReimbursementSettings = async ({ domain, companyId, config, signal }) => {
  if (!config) {
    const data = await request(domain, `${CONFIG_BASE}settings/`, { query: { company_id: companyId }, signal });
    const loaded = fromApiSettings(data);
    setCachedPayInputConfig(domain, companyId, REIMBURSEMENT_PAY_INPUT_TYPE, loaded);
    return { config: loaded };
  }
  const result = await request(domain, `${CONFIG_BASE}settings/`, {
    method: "PUT",
    signal,
    body: {
      company_id: Number(companyId),
      is_enabled: Boolean(config.enabled),
      default_treatment: config.treatment,
      approval_required: config.approvalRequired === "yes",
      allowed_sources: Object.fromEntries(config.sourceOptions.map(({ value, comingSoon }) => [
        value, !comingSoon && value !== "ERP" && config.sources.includes(value),
      ])),
    },
  });
  const saved = fromApiSettings(result?.data || {});
  setCachedPayInputConfig(domain, companyId, REIMBURSEMENT_PAY_INPUT_TYPE, saved);
  return { config: saved, message: result?.message };
};

// ---------- Configuration: types and payslip lines ----------

export const fromApiType = (item) => ({
  id: item.id,
  name: item.name || "",
  helpText: item.help_text || "",
  limitPerClaim: toNumber(item.limit_per_claim),
  limitPerMonth: toNumber(item.limit_per_month),
  billWithinDays: toNumber(item.bill_within_days),
  billNeeded: Boolean(item.bill_required),
  payComponentId: item.pay_component_id ?? "",
  payslipLine: item.pay_component_name || "",
  active: Boolean(item.is_active),
  used: Boolean(item.in_use),
  hint: item.hint || "",
  sortOrder: item.sort_order ?? 0,
});

const toApiType = (values) => ({
  name: values.name,
  help_text: values.helpText || null,
  limit_per_claim: toAmountString(values.limitPerClaim),
  limit_per_month: toAmountString(values.limitPerMonth),
  bill_within_days: Number(values.billWithinDays),
  bill_required: Boolean(values.billNeeded),
  pay_component_id: Number(values.payComponentId),
  is_active: Boolean(values.active),
});

export const fetchReimbursementTypes = async ({ domain, companyId, activeOnly = false, signal }) => {
  const data = await request(domain, `${CONFIG_BASE}types/`, {
    query: { company_id: companyId, page_size: 200, ...(activeOnly ? { is_active: "true" } : {}) },
    signal,
  });
  const rows = Array.isArray(data) ? data : data?.results || [];
  return rows.map(fromApiType);
};

export const createReimbursementType = async ({ domain, companyId, values }) => {
  const result = await request(domain, `${CONFIG_BASE}types/`, {
    method: "POST",
    body: { company_id: Number(companyId), ...toApiType(values) },
  });
  return { type: fromApiType(result?.data || {}), message: result?.message };
};

export const updateReimbursementType = async ({ domain, id, values }) => {
  const result = await request(domain, `${CONFIG_BASE}types/${id}/`, { method: "PATCH", body: toApiType(values) });
  return { type: fromApiType(result?.data || {}), message: result?.message };
};

export const deleteReimbursementType = ({ domain, id }) =>
  request(domain, `${CONFIG_BASE}types/${id}/`, { method: "DELETE" });

export const fetchPayslipLines = async ({ domain, companyId, signal }) => {
  const data = await request(domain, `${CONFIG_BASE}pay-components/`, { query: { company_id: companyId }, signal });
  return (Array.isArray(data) ? data : data?.results || []).map((item) => ({
    value: item.id,
    label: item.payslip_name || item.name,
  }));
};

// Active types for the claim forms (same table as the claims, so the ids
// work for submit). Kept per company for the session.
const activeTypesCache = new Map();
export const useActiveReimbursementTypes = (domain, companyId) => {
  const key = `${domain}|${companyId}`;
  const validCompany = Number(companyId) > 0;
  const [state, setState] = useState(() => ({ key, types: activeTypesCache.get(key) || [], loading: false, error: "" }));
  useEffect(() => {
    if (!domain || !validCompany) {
      setState({ key, types: [], loading: false, error: "" });
      return undefined;
    }
    const controller = new AbortController();
    setState({ key, types: activeTypesCache.get(key) || [], loading: !activeTypesCache.has(key), error: "" });
    fetchReimbursementTypes({ domain, companyId, activeOnly: true, signal: controller.signal })
      .then((types) => {
        activeTypesCache.set(key, types);
        if (!controller.signal.aborted) setState({ key, types, loading: false, error: "" });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setState({ key, types: activeTypesCache.get(key) || [], loading: false, error: error.message });
      });
    return () => controller.abort();
  }, [domain, companyId, key, validCompany]);
  return state.key === key ? state : { key, types: [], loading: true, error: "" };
};

// Switches from the reimbursement settings: Enable Reimbursements and bill
// photo upload (both default on, and stay on if the settings can't be
// loaded). Login is enough to read them.
export const useReimbursementSettings = (domain, companyId) => {
  const [flags, setFlags] = useState({ enabled: true, billUploadEnabled: true });
  useEffect(() => {
    if (!domain || !(Number(companyId) > 0)) return undefined;
    const controller = new AbortController();
    requestReimbursementSettings({ domain, companyId, signal: controller.signal })
      .then(({ config }) => {
        if (!controller.signal.aborted) setFlags({ enabled: config.enabled, billUploadEnabled: config.billUploadEnabled });
      })
      .catch(() => {});
    return () => controller.abort();
  }, [domain, companyId]);
  return flags;
};

export const useReimbursementBillUpload = (domain, companyId) =>
  useReimbursementSettings(domain, companyId).billUploadEnabled;

// ---------- Claims ----------

// "03-11-2026" or "03-11-2026, 10:42" -> "2026-11-03" / "2026-11-03 10:42".
const parseApiDate = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{2})-(\d{2})-(\d{4})(?:,?\s*(\d{2}):(\d{2}))?/);
  if (match) {
    const [, dd, mm, yyyy, hh, min] = match;
    return hh ? `${yyyy}-${mm}-${dd} ${hh}:${min}` : `${yyyy}-${mm}-${dd}`;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : null;
};

const parseDay = (value) => {
  const parsed = parseApiDate(value);
  return parsed ? parsed.slice(0, 10) : null;
};

// pay_month may come as "2026-11", "2026-11-01" or "Nov 2026".
const parsePayMonth = (value) => {
  if (!value) return null;
  const text = String(value);
  if (/^\d{4}-\d{2}/.test(text)) return text.slice(0, 7);
  const parsed = dayjs(`01 ${text}`);
  return parsed.isValid() ? parsed.format("YYYY-MM") : null;
};

export const claimRequestId = (id) => `RB-${String(id).padStart(5, "0")}`;

export const fromApiClaim = (item, { self, types = [] } = {}) => {
  const status = item.status || "Pending";
  const type = types.find((entry) => entry.id === item.expense_type_id) ||
    types.find((entry) => entry.name.trim().toLowerCase() === String(item.expense_type || "").trim().toLowerCase()) || null;
  return {
    id: item.id,
    requestId: item.request_id || claimRequestId(item.id),
    self,
    userId: item.user,
    employee: {
      id: item.user,
      name: item.user_name || "",
      code: item.employee_id || "",
      department: item.department_name || "",
      designation: item.designation || "",
      photo: item.image_url || "",
    },
    typeId: item.expense_type_id ?? type?.id ?? null,
    typeName: item.expense_type || type?.name || "",
    payslipLine: item.payslip_line || type?.payslipLine || "",
    purpose: item.description || "",
    comments: item.comments || "",
    paidBy: item.paid_by || "Employee",
    billDate: parseDay(item.bill_date || item.date_incurred),
    billNumber: item.bill_number || "",
    attachments: (item.attachments || []).map((file) => ({
      id: file.id,
      name: file.original_name || `bill-${file.id}`,
      url: file.url,
      contentType: file.content_type || "",
    })),
    amount: Number(item.amount || 0),
    amountApproved: toNumber(item.amount_approved),
    status,
    submittedAt: parseApiDate(item.applied_on),
    submittedBy: item.applied_by || "",
    approvedAt: parseApiDate(item.approvedat),
    approvedBy: item.approvedby || "",
    rejectedAt: parseApiDate(item.rejectedat),
    rejectedBy: item.rejectedby || "",
    rejectReason: status === "Rejected" ? item.reason || "" : "",
    cancelledAt: parseApiDate(item.cancelledat),
    cancelledBy: item.cancelledby || "",
    cancelReason: status === "Cancelled" ? item.reason || "" : "",
    payMonth: parsePayMonth(item.pay_month),
    addedToPayrollAt: parseApiDate(item.added_to_payroll_at),
    paidAt: parseApiDate(item.paid_at),
  };
};

// My claims: every page (100 a page) so the status cards, date range and
// monthly limit check see all of them.
export const fetchMyClaims = async ({ domain, companyId, userId, signal }) => {
  const rows = [];
  for (let page = 1; page <= 50; page += 1) {
    const data = await request(domain, "expense/", {
      query: { selected_user_id: userId, company_id: companyId, page, page_size: 100 },
      signal,
    });
    if (Array.isArray(data)) return data;
    rows.push(...(data?.results || []));
    if (!data?.next) break;
  }
  return rows;
};

export const fetchTeamClaims = async ({ domain, companyId, signal }) => {
  const data = await request(domain, TEAM_CLAIMS, { query: { company_id: companyId }, signal });
  return Array.isArray(data) ? data : data?.results || [];
};

// Submit a claim (multipart, bills as repeated "attachments"). bill_number is
// sent already; the server saves it once the field is added.
export const submitClaim = async ({ domain, companyId, values, files = [], approve = false }) => {
  const form = new FormData();
  const fields = {
    user: values.userId,
    expense_type: values.typeId,
    description: values.purpose,
    amount: Number(values.amount).toFixed(2),
    date_incurred: values.billDate,
    bill_date: values.billDate,
    bill_number: values.billNumber,
    paid_by: "Employee",
    ...(approve ? { action: "Approved" } : {}),
  };
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") form.append(key, value);
  });
  files.forEach((file) => form.append("attachments", file));
  return request(domain, "expense/", { method: "POST", query: { company_id: companyId }, form });
};

// Edit a Pending claim: PUT expense/{id}/ (same call the old Expense Claims
// edit used). JSON normally; multipart when a new bill photo is added.
export const updateClaim = ({ domain, companyId, id, values, files = [] }) => {
  const fields = {
    expense_type: values.typeId,
    description: values.purpose,
    amount: Number(values.amount).toFixed(2),
    date_incurred: values.billDate,
    bill_date: values.billDate,
    bill_number: values.billNumber,
    paid_by: "Employee",
  };
  const query = { company_id: companyId };
  if (!files.length) return request(domain, `expense/${id}/`, { method: "PUT", query, body: fields });
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, value);
  });
  files.forEach((file) => form.append("attachments", file));
  return request(domain, `expense/${id}/`, { method: "PUT", query, form });
};

export const cancelOwnClaim = ({ domain, companyId, id, reason }) =>
  request(domain, `expense/${id}/`, {
    method: "PUT",
    query: { company_id: companyId },
    body: { status: "Cancelled", ...(reason ? { reason } : {}) },
  });

// Team review (Approved / Rejected / Paid / Cancelled) of one or many claims.
// Returns { message, updated_ids, skipped_ids, not_found_ids }.
export const reviewClaims = ({ domain, companyId, ids, status, reason }) =>
  request(domain, TEAM_CLAIMS, {
    method: "PUT",
    query: { company_id: companyId },
    body: {
      ...(ids.length === 1 ? { id: Number(ids[0]) } : { ids: ids.map(Number) }),
      status,
      ...(reason ? { reason } : {}),
    },
  });

// Toast text for a review: changed claims, plus the ones the server skipped
// (only Pending claims, never the caller's own, can change today).
export const reviewOutcome = (result, verb) => {
  const updated = result?.updated_ids?.length || 0;
  const skipped = (result?.skipped_ids?.length || 0) + (result?.not_found_ids?.length || 0);
  if (!updated) {
    return { type: "error", text: `No claim was ${verb}. Only pending claims of other employees can be changed.` };
  }
  return {
    type: skipped ? "info" : "success",
    text: `${updated} claim(s) ${verb}.${skipped ? ` ${skipped} skipped (not pending or not found).` : ""}`,
  };
};

// Bills need the login token, so a plain link can't open them: fetch the
// file and open it in a new tab.
export const openClaimAttachment = async (url) => {
  const tab = window.open("", "_blank");
  try {
    const response = await fetch(url, { headers: { Authorization: `Token ${localStorage.getItem("token")}` } });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw parseError(body, response.status);
    }
    const blobUrl = URL.createObjectURL(await response.blob());
    if (tab) tab.location.href = blobUrl;
    else window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    tab?.close();
    throw error;
  }
};
